import { createSelector } from 'reselect'
import { denormalize } from 'normalizr'
import { values, flatten, get, forEach, find, isNil, compact } from 'lodash'

import { DEFAULT_LIST_KEY } from 'store/common/constants/generic'

/**
 * Get current store state
 *
 * @private
 * @returns {function}
 *
 * @example:
 *
 * const getState = makeGetState()
 */
export const makeGetState = () => state => state

/**
 * Get current options, which were passed to function born from selector creator
 *
 * @private
 * @returns {function}
 *
 * @example:
 *
 * const getOptions = makeGetOptions()
 */
export const makeGetOptions = () => (state, options) => options

/**
 * Get all items from store entities by key as a collection
 *
 * @returns {function}
 *
 * @example:
 *
 * const getStatics = makeGetStatics()
 */
export const makeGetStatics = () => state => get(state, 'statics', {})

/**
 * Get statics from store
 *
 * @param staticEntityKey - {string}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getMe = makeGetStaticsEntity({ staticEntityKey: 'me' })
 */
export const makeGetStaticsEntity = ({ staticEntityKey }) => {
  const getStaticsSelector = makeGetStatics()

  return createSelector([getStaticsSelector], staticEntity => get(staticEntity, staticEntityKey))
}

/**
 * Get all items from store entities by key as a collection
 *
 * @param entityKey - {string}
 * @param [schema] - {object}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUsersCollection = makeGetEntitiesCollection({ entityKey: 'users' })
 */
export const makeGetEntitiesCollection =
  ({ entityKey, schema }) =>
  state => {
    const entities = get(state, `entities.${entityKey}`, {})
    const entitiesDenormalized = {}

    if (isNil(schema)) {
      return entities
    }

    forEach(entities, (entity, key) => {
      entitiesDenormalized[key] = denormalize(entity, schema, state.entities)
    })

    return entitiesDenormalized
  }

/**
 * Get all items from store entities by key as an array
 *
 * @param entityKey - {string}
 * @param [schema] - {object}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUsersCollection = makeGetEntitiesCollection({ entityKey: 'users', [schema] })
 */
export const makeGetEntitiesArray =
  ({ entityKey, schema }) =>
  state => {
    const entities = values(get(state, `entities.${entityKey}`, {})) || []
    const entitiesDenormalized = []

    if (isNil(schema)) {
      return entities
    }

    forEach(entities, entity => {
      entitiesDenormalized.push(denormalize(entity, schema, state.entities))
    })

    return entitiesDenormalized
  }

/**
 * Get all list pages from store for particular entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getAbsencesPages = makeGetEntityListPages({ entityKey: 'absences' })
 * const getAbsencesHistoryPages = makeGetEntityListPages({ entityKey: 'absences', entityListKey: 'history' })
 */
export const makeGetEntityListPages =
  ({ entityKey, entityListKey = DEFAULT_LIST_KEY }) =>
  state =>
    get(state, `lists.${entityKey}.${entityListKey}.pages`, {})

/**
 * Get all list meta from store for particular entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getAbsencesMeta = makeGetEntityListMeta({ entityKey: 'absences' })
 * const getAbsencesHistoryMeta = makeGetEntityListMeta({ entityKey: 'absences', entityListKey: 'history' })
 */
export const makeGetEntityListMeta =
  ({ entityKey, entityListKey = DEFAULT_LIST_KEY }) =>
  state =>
    get(state, `lists.${entityKey}.${entityListKey}.meta`, {})

/**
 * Get list of ids for all pages for particular entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getAbsencesMeta = makeGetEntityListMeta({ entityKey: 'absences' })
 * const getAbsencesHistoryMeta = makeGetEntityListMeta({ entityKey: 'absences', entityListKey: 'history' })
 */
export const makeGetEntityListIds = ({ entityKey, entityListKey = DEFAULT_LIST_KEY }) => {
  const getEntityListPages = makeGetEntityListPages({ entityKey, entityListKey })

  return createSelector([getEntityListPages], listPages => flatten(values(listPages)))
}

/**
 * Get entity from store by Id
 *
 * @param entityKey - {string}
 * @param pristineData - {object}
 * @param schema - {function}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUserById = makeGetEntityById({ entityKey: 'users', pristineData: {}, schema: userSchema  })
 *
 * export const mapStateToProps = state => ({
 *    user: userSelectors.getUserById(state, {
 *      id: get(state, 'router.params.id')
 *    }),
 * })
 */
export const makeGetEntityById = ({ entityKey, pristineData = {}, schema }) => {
  const getState = makeGetState()
  const getOptions = makeGetOptions()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })

  return createSelector([getState, getOptions, getEntitiesCollectionSelector], (state, options, entities) => {
    const id = get(options, 'id', 0)
    const entity = get(entities, `${id}`, pristineData)

    return denormalize(entity, schema, state.entities)
  })
}

/**
 * Get entity from store by Filter
 *
 * @param entityKey - {string}
 * @param pristineData - {object}
 * @param schema - {function}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUserByFilter = makeGetEntityByFilter({ entityKey: 'users', pristineData: {}, schema: userSchema  })
 *
 * export const mapStateToProps = state => ({
 *    user: userSelectors.getUserByFilter(state, {
 *      filter: {
 *        admin: true
 *      }
 *    }),
 * })
 */
export const makeGetEntityByFilter = ({ entityKey, pristineData = {}, schema }) => {
  const getState = makeGetState()
  const getOptions = makeGetOptions()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })

  return createSelector([getState, getOptions, getEntitiesCollectionSelector], (state, options, entities) => {
    const filterParams = get(options, 'filter', {})
    const entity = find(entities, filterParams) || pristineData

    return denormalize(entity, schema, state.entities)
  })
}

/**
 * Get all entities from all available pages in the list by entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 * @param schema - {function}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUsers = makeGetEntitiesArrayFromList({ entityKey: 'users' schema: userSchema  })
 * const getDeletedUsers = makeGetEntitiesArrayFromList({
 *    entityKey: 'users', entityListKey: 'deleted', schema: userSchema
 * })
 *
 * export const mapStateToProps = state => ({
 *    users: userSelectors.getUsers(state),
 *    usersWithStatusDeleted: userSelectors.getDeletedUsers(state),
 * })
 */
export const makeGetEntitiesArrayFromList = ({ entityKey, entityListKey = DEFAULT_LIST_KEY, schema }) => {
  const getState = makeGetState()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })
  const getEntityListIds = makeGetEntityListIds({ entityKey, entityListKey })

  return createSelector([getState, getEntitiesCollectionSelector, getEntityListIds], (state, entities, ids) =>
    compact(ids.map(id => denormalize(entities[id], schema, state.entities))),
  )
}

/**
 * Returns collection of entities by pages
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 * @param schema - {function}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUsers = makeGetEntitiesCollectionFromList({ entityKey: 'users' schema: userSchema  })
 * const getDeletedUsers = makeGetEntitiesCollectionFromList({
 *    entityKey: 'users', entityListKey: 'deleted', schema: userSchema
 * })
 *
 * export const mapStateToProps = state => ({
 *    users: userSelectors.getUsers(state),
 *    usersWithStatusDeleted: userSelectors.getDeletedUsers(state),
 * })
 */
export const makeGetEntitiesCollectionFromList = ({ entityKey, entityListKey = DEFAULT_LIST_KEY, schema }) => {
  const getState = makeGetState()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })
  const getEntityListIds = makeGetEntityListIds({ entityKey, entityListKey })

  return createSelector([getState, getEntitiesCollectionSelector, getEntityListIds], (state, entities, ids) => {
    const entitiesDenormalized = {}

    forEach(ids, id => {
      entitiesDenormalized[id] = denormalize(entities[id], schema, state.entities)
    })

    return entitiesDenormalized
  })
}

/**
 * Get entities by page in the list by entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 * @param schema - {function}
 *
 * @returns {function}
 *
 * @example:
 *
 * const getUsersByPage = makeGetEntitiesArrayFromListByPage({ entityKey: 'users' schema: userSchema  })
 * const getDeletedUsersByPage = makeGetEntitiesArrayFromListByPage({
 *    entityKey: 'users', entityListKey: 'deleted', schema: userSchema
 * })
 *
 * export const mapStateToProps = state => ({
 *    users: userSelectors.getUsersByPage(state, {
 *      page: get(state, 'router.params.page')
 *    }),
 *    usersWithStatusDeleted: userSelectors.getDeletedUsersByPage(state, {
 *      page: get(state, 'router.params.page')
 *    })
 * })
 */
export const makeGetEntitiesArrayFromListByPage = ({ entityKey, entityListKey = DEFAULT_LIST_KEY, schema }) => {
  const getState = makeGetState()
  const getOptions = makeGetOptions()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })
  const getEntityListPages = makeGetEntityListPages({ entityKey, entityListKey })

  return createSelector(
    [getState, getOptions, getEntitiesCollectionSelector, getEntityListPages],
    (state, options, entities, pages) => {
      const page = get(options, 'page', 0)
      const ids = pages[page] || []

      return compact(ids.map(id => denormalize(entities[id], schema, state.entities)))
    },
  )
}

/**
 * Get entities before page in the list by entityKey and listType
 *
 * @param entityKey - {string}
 * @param entityListKey - {string}
 * @param schema - {function}
 * @returns {function}
 * @example:
 *
 * const getUsersByPage = makeGetEntitiesArrayFromListBeforePage({ entityKey: 'users' schema: userSchema  })
 * const getDeletedUsersByPage = makeGetEntitiesArrayFromListBeforePage({
 *    entityKey: 'users', entityListKey: 'deleted', schema: userSchema
 * })
 *
 * export const mapStateToProps = state => ({
 *    users: userSelectors.getUsersByPage(state, {
 *      page: get(state, 'router.params.page')
 *    }),
 *    usersWithStatusDeleted: userSelectors.getDeletedUsersByPage(state, {
 *      page: get(state, 'router.params.page')
 *    })
 * })
 */
export const makeGetEntitiesArrayFromListBeforePage = ({ entityKey, entityListKey = DEFAULT_LIST_KEY, schema }) => {
  const getState = makeGetState()
  const getOptions = makeGetOptions()
  const getEntitiesCollectionSelector = makeGetEntitiesCollection({ entityKey })
  const getEntityListPages = makeGetEntityListPages({ entityKey, entityListKey })

  return createSelector(
    [getState, getOptions, getEntitiesCollectionSelector, getEntityListPages],
    (state, options, entities, pages) => {
      const page = get(options, 'page', 0)
      const ids = []

      forEach(pages, (value, key) => {
        if (key <= page) {
          ids.push(...value)
        }
      })

      return compact(ids.map(id => denormalize(entities[id], schema, state.entities)))
    },
  )
}
